import { Badge } from '@/components/ui/badge';
import { Shield, ShieldAlert, ShieldQuestion } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ConfidenceLevel, CheckMethod } from '@/types/service';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ConfidenceBadgeProps {
  confidence: ConfidenceLevel;
  method: CheckMethod;
  statusDescription?: string;
  serviceUrl?: string;
}

const confidenceConfig = {
  high: {
    icon: Shield,
    label: 'High Confidence',
    description: 'Status verified from official API',
  },
  medium: {
    icon: Shield,
    label: 'Medium Confidence',
    description: 'Status parsed from HTML page',
  },
  low: {
    icon: ShieldAlert,
    label: 'Low Confidence',
    description: 'Status based on HTTP response only',
  },
};

const methodLabels: Record<CheckMethod, string> = {
  'api': 'API Check',
  'html-parse': 'HTML Parse',
  'http-status': 'HTTP Status',
  'none': 'No URL',
};

export const ConfidenceBadge = ({ confidence, method, statusDescription, serviceUrl }: ConfidenceBadgeProps) => {
  const config = confidenceConfig[confidence];
  const Icon = config.icon;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge 
          variant="outline" 
          className="gap-1 cursor-help"
        >
          <Icon className="h-3 w-3" />
          <span className="text-xs">
            {methodLabels[method]}
          </span>
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm">
        <div className="space-y-2">
          <div className="font-semibold">{config.label}</div>
          <div className="text-sm text-muted-foreground">
            {config.description}
          </div>
          {serviceUrl && (
            <div className="text-xs font-mono text-muted-foreground pt-2 border-t break-all">
              Source: {serviceUrl}
            </div>
          )}
          {statusDescription && (
            <div className="text-xs text-muted-foreground pt-2 border-t">
              {statusDescription}
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
